import * as THREE from "three";

import { ApiClient } from "../../../../../utils/apiClient";
import { shouldCacheTextureUrl } from "../../../utils/media-cache-policy.utils";

const BASE_API = process.env.REACT_APP_BASE_API || '';
const apiClient = ApiClient(BASE_API);

class TextureManager {
    private static instance: TextureManager;

    private constructor() { }

    public static getInstance(): TextureManager {
        if (!TextureManager.instance) {
            TextureManager.instance = new TextureManager();
        }
        return TextureManager.instance;
    }

    public async loadTexture(loader: THREE.TextureLoader | null, url: string, cache: any, options: { cache?: boolean } = {}) {
        try {
            return await new Promise((resolve, reject) => {
                try {
                    if (!loader || typeof loader.load !== "function") {
                        resolve(null);
                        return;
                    }

                    if (url && url.length > 0) {
                        const shouldCache = options.cache ?? shouldCacheTextureUrl(url);
                        if (shouldCache && cache[url]) {
                            resolve(cache[url].clone ? cache[url].clone() : cache[url]);
                            return;
                        }

                        // Use proxy for external images to avoid CORS issues
                        const finalUrl = apiClient.getProxiedImageUrl(url);

                        loader.load(finalUrl, (img) => {
                            if (shouldCache) {
                                cache[url] = img;
                                resolve(img.clone ? img.clone() : img);
                                return;
                            }
                            resolve(img);
                        }, () => { }, (err) => {
                            console.error(`TextureManager: Error loading texture from ${url}`, err);
                            reject(err);
                        });
                    } else {
                        resolve(null);
                    }
                } catch (error) {
                    reject(error);
                }
            });
        } catch (error) {
            console.error("TextureManager: Error in loadTexture", error);
            throw error;
        }
    }

    public async loadTextureWithProps(child: any, mapType: string, texturePath: string, loader: THREE.TextureLoader, cache: any): Promise<void> {
        try {
            const img: any = await this.loadTexture(loader, `/assets/${texturePath}`, cache, { cache: true });
            if (img) {
                img.wrapS = img.wrapT = THREE.RepeatWrapping;
                img.offset.set(0, 0);
                img.repeat.set(1, 1);
                img.colorSpace = THREE.SRGBColorSpace;
                child.material[mapType] = img;
                child.material.needsUpdate = true;
            }
        } catch (error) {
            console.error(`TextureManager: Error in loadTextureWithProps for ${texturePath}`, error);
        }
    }
}

const textureManager = TextureManager.getInstance();
export const loadTexture = textureManager.loadTexture.bind(textureManager);
export const loadTextureWithProps = textureManager.loadTextureWithProps.bind(textureManager);
